import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Youtube, Instagram, Facebook, Mail, MapPin } from 'lucide-react';
import { pagesAPI, branchesAPI } from '../api';
import { useSettings } from '../contexts/SettingsContext';

const Footer = () => {
  const { settings } = useSettings();
  const [customPages, setCustomPages] = useState([]);
  const [cities, setCities] = useState([]);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [pagesRes, branchesRes] = await Promise.all([
        pagesAPI.getAll(),
        branchesAPI.getAll(),
      ]);
      // Show only active pages that should be in menu (footer)
      setCustomPages(pagesRes.data.filter(p => p.is_active && p.show_in_menu));
      
      // Extract unique cities from active branches
      const activeBranches = branchesRes.data.filter(branch => branch.is_active);
      const uniqueCities = [...new Set(activeBranches.map(branch => branch.city))];
      setCities(uniqueCities.sort());
    } catch (error) {
      console.error('Error loading footer data:', error);
    }
  };
  
  if (!settings) return null;
  
  return (
    <footer className="bg-black border-t border-gray-800 mt-20">
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* About */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">{settings.site_name || 'Cinefora'}</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              {settings.footer_description || "Türkiye'nin önde gelen sinema zinciri. En yeni filmleri en iyi teknoloji ile izleme keyfi."}
            </p>
            <div className="flex gap-4 mt-4">
              {settings.social_youtube && (
                <a href={settings.social_youtube} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FDB714] transition-colors">
                  <Youtube size={20} />
                </a>
              )}
              {settings.social_instagram && (
                <a href={settings.social_instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FDB714] transition-colors">
                  <Instagram size={20} />
                </a>
              )}
              {settings.social_twitter && (
                <a href={settings.social_twitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FDB714] transition-colors">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
              )}
              {settings.social_facebook && (
                <a href={settings.social_facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FDB714] transition-colors">
                  <Facebook size={20} />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">Hızlı Erişim</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/vizyondaki-filmler" className="text-gray-400 hover:text-[#FDB714] transition-colors text-sm">Vizyondakiler</Link>
              </li>
              <li>
                <Link to="/yakinda" className="text-gray-400 hover:text-[#FDB714] transition-colors text-sm">Yakında</Link>
              </li>
              <li>
                <Link to="/kampanyalar" className="text-gray-400 hover:text-[#FDB714] transition-colors text-sm">Kampanyalar</Link>
              </li>
              <li>
                <Link to="/haberler" className="text-gray-400 hover:text-[#FDB714] transition-colors text-sm">Haberler</Link>
              </li>
              <li>
                <Link to="/salonlarimiz" className="text-gray-400 hover:text-[#FDB714] transition-colors text-sm">Salonlarımız</Link>
              </li>
            </ul>
          </div>

          {/* Corporate */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">Kurumsal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/hakkimizda" className="text-gray-400 hover:text-[#FDB714] transition-colors text-sm">Hakkımızda</Link>
              </li>
              {customPages.map((page) => (
                <li key={page._id}>
                  <Link to={`/page/${page.slug}`} className="text-gray-400 hover:text-[#FDB714] transition-colors text-sm">
                    {page.title}
                  </Link>
                </li>
              ))}
              {settings.show_kariyer_link && (
                <li>
                  <Link to="/kariyer" className="text-gray-400 hover:text-[#FDB714] transition-colors text-sm">Kariyer</Link>
                </li>
              )}
              {settings.show_kvkk_link && (
                <li>
                  <Link to="/kvkk" className="text-gray-400 hover:text-[#FDB714] transition-colors text-sm">KVKK</Link>
                </li>
              )}
              {settings.show_kullanim_kosullari_link && (
                <li>
                  <Link to="/kullanim-kosullari" className="text-gray-400 hover:text-[#FDB714] transition-colors text-sm">Kullanım Koşulları</Link>
                </li>
              )}
              {settings.show_gizlilik_politikasi_link && (
                <li>
                  <Link to="/gizlilik-politikasi" className="text-gray-400 hover:text-[#FDB714] transition-colors text-sm">Gizlilik Politikası</Link>
                </li>
              )}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">İletişim</h3>
            <ul className="space-y-3">
              {settings.contact_email && (
                <li className="flex items-start gap-2 text-gray-400 text-sm">
                  <Mail size={16} className="mt-1 flex-shrink-0" />
                  <span>{settings.contact_email}</span>
                </li>
              )}
              {cities.length > 0 && (
                <li className="flex items-start gap-2 text-gray-400 text-sm">
                  <MapPin size={16} className="mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-gray-300 mb-1">
                      Türkiye genelinde {cities.length} Lokasyonda
                    </div>
                    <div className="font-medium text-gray-300 mb-1">Salonlarımızin Bulunduğu Şehirler:</div>
                    <div className="flex flex-wrap gap-1">
                      {cities.map((city, index) => (
                        <span key={city}>
                          {city}
                          {index < cities.length - 1 && <span className="text-gray-500"> • </span>}
                        </span>
                      ))}
                    </div>
                  </div>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-6 mt-6">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <p className="text-gray-500 text-sm">
              © 2025 {settings.site_name || 'Cinefora'} Sinemaları. Tüm hakları saklıdır.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
